import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Award, University, FileCheck, Globe } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import ImageUpload from '../../components/admin/ImageUpload';

const PartnershipsManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title_ar: '',
    title_en: '',
    description_ar: '',
    description_en: '',
    type: 'university',
    country: '',
    logo: '',
    certificate_image: '',
    website_url: '',
    agreement_date: '',
    is_published: true,
    order: 0
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/partnerships/all`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      setItems(await response.json());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing ? `${BACKEND_URL}/api/partnerships/${editing}` : `${BACKEND_URL}/api/partnerships/`;
      const response = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        fetchItems();
        resetForm();
        alert('تم الحفظ بنجاح! ✅');
      }
    } catch (error) {
      console.error(error);
      alert('حدث خطأ');
    }
  };

  const handleEdit = (item) => {
    setEditing(item.id);
    setFormData({
      title_ar: item.title_ar || '',
      title_en: item.title_en || '',
      description_ar: item.description_ar || '',
      description_en: item.description_en || '',
      type: item.type || 'university',
      country: item.country || '',
      logo: item.logo || '',
      certificate_image: item.certificate_image || '',
      website_url: item.website_url || '',
      agreement_date: item.agreement_date || '',
      is_published: item.is_published !== undefined ? item.is_published : true,
      order: item.order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الشراكة؟')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/partnerships/${item.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        fetchItems();
        alert('تم الحذف بنجاح!');
      }
    } catch (error) {
      console.error(error);
      alert('حدث خطأ');
    }
  };

  const resetForm = () => {
    setFormData({
      title_ar: '',
      title_en: '',
      description_ar: '',
      description_en: '',
      type: 'university',
      country: '',
      logo: '',
      certificate_image: '',
      website_url: '',
      agreement_date: '',
      is_published: true,
      order: 0
    });
    setEditing(null);
    setShowForm(false);
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'university': return <University className="w-5 h-5" />;
      case 'certificate': return <Award className="w-5 h-5" />;
      case 'accreditation': return <FileCheck className="w-5 h-5" />;
      case 'organization': return <Globe className="w-5 h-5" />;
      default: return <Globe className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      university: 'شراكة جامعية',
      certificate: 'شهادة',
      accreditation: 'اعتماد',
      organization: 'منظمة'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      university: 'bg-blue-100 text-blue-700',
      certificate: 'bg-green-100 text-green-700',
      accreditation: 'bg-purple-100 text-purple-700',
      organization: 'bg-amber-100 text-amber-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-[#0F388E] flex items-center gap-3">
          <Award className="w-10 h-10" />
          الاتفاقيات والشهادات الدولية
        </h1>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="bg-[#0F388E]">
            <Plus className="ml-2 w-5 h-5" />
            إضافة شراكة
          </Button>
        )}
      </div>

      {!showForm && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(item => (
            <Card key={item.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                {item.logo && (
                  <img 
                    src={item.logo} 
                    alt={item.title_ar} 
                    className="w-full h-40 object-contain bg-gray-50 rounded-lg mb-4 p-2"
                  />
                )}
                
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getTypeColor(item.type)}`}>
                    {getTypeIcon(item.type)}
                    {getTypeLabel(item.type)}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-[#0F388E] mb-2">{item.title_ar}</h3>
                {item.country && (
                  <p className="text-sm text-gray-500 mb-2">📍 {item.country}</p>
                )}
                {item.description_ar && (
                  <p className="text-sm text-gray-600 line-clamp-3 mb-4">{item.description_ar}</p>
                )}

                <div className="flex gap-2 justify-end border-t pt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(item)}
                    className="text-[#0F388E]"
                  >
                    <Edit2 className="w-4 h-4 ml-1" />
                    تعديل
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(item)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 ml-1" />
                    حذف
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {items.length === 0 && (
            <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg">
              <Award className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">لا توجد شراكات مضافة بعد</p>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-6 h-6" />
              {editing ? 'تعديل شراكة' : 'إضافة شراكة جديدة'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Type Selection */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100">
                <h3 className="font-bold text-[#0F388E] text-lg mb-4">النوع</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { value: 'university', label: 'شراكة جامعية', icon: <University className="w-5 h-5" /> },
                    { value: 'certificate', label: 'شهادة', icon: <Award className="w-5 h-5" /> },
                    { value: 'accreditation', label: 'اعتماد', icon: <FileCheck className="w-5 h-5" /> },
                    { value: 'organization', label: 'منظمة', icon: <Globe className="w-5 h-5" /> }
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({...formData, type: type.value})}
                      className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                        formData.type === type.value
                          ? 'border-[#0F388E] bg-[#0F388E] text-white'
                          : 'border-gray-200 hover:border-[#0F388E] bg-white'
                      }`}
                    >
                      {type.icon}
                      <span className="text-sm font-medium">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Basic Info */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg space-y-4 border border-green-100">
                <h3 className="font-bold text-[#0F388E] text-lg">المعلومات الأساسية</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      العنوان بالعربي *
                    </label>
                    <input
                      type="text"
                      value={formData.title_ar}
                      onChange={(e) => setFormData({...formData, title_ar: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title in English
                    </label>
                    <input
                      type="text"
                      value={formData.title_en}
                      onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      الدولة
                    </label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({...formData, country: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="مثال: ماليزيا، الأردن، تركيا"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      تاريخ الاتفاقية
                    </label>
                    <input
                      type="text"
                      value={formData.agreement_date}
                      onChange={(e) => setFormData({...formData, agreement_date: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="2024"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      رابط الموقع
                    </label>
                    <input
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg space-y-4 border border-purple-100">
                <h3 className="font-bold text-[#0F388E] text-lg">الوصف</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الوصف بالعربي
                  </label>
                  <textarea
                    value={formData.description_ar}
                    onChange={(e) => setFormData({...formData, description_ar: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description in English
                  </label>
                  <textarea
                    value={formData.description_en}
                    onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                </div>
              </div>

              {/* Images */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-6 rounded-lg space-y-4 border border-amber-100">
                <h3 className="font-bold text-[#0F388E] text-lg">الصور</h3>
                <ImageUpload
                  label="الشعار/اللوجو"
                  value={formData.logo}
                  onChange={(v) => setFormData({...formData, logo: v})}
                />
                <ImageUpload
                  label="صورة الشهادة (إن وجدت)"
                  value={formData.certificate_image}
                  onChange={(v) => setFormData({...formData, certificate_image: v})}
                />
              </div>

              {/* Settings */}
              <div className="bg-gray-50 p-6 rounded-lg space-y-4">
                <h3 className="font-bold text-[#0F388E] text-lg">الإعدادات</h3>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_published"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                    className="w-5 h-5 text-[#0F388E] border-gray-300 rounded focus:ring-[#0F388E]"
                  />
                  <label htmlFor="is_published" className="text-gray-700">
                    نشر الشراكة على الموقع
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>
                  إلغاء
                </Button>
                <Button type="submit" className="bg-[#0F388E]">
                  {editing ? 'تحديث الشراكة' : 'حفظ الشراكة'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PartnershipsManagement;
